import React, { useEffect, useState } from "react";
import RoleRateFields from "./RoleRateFields";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { createYupSchema } from "../../../utilities/yupSchemaCreator";
import { RolesEnum } from "../../../utilities/Enum";
import { useSite } from "../../Sites/Provider";
import { useEmployees } from "../Provider";

const yupSchema = RoleRateFields.reduce(createYupSchema, {});
const formSchema = Yup.object().shape(yupSchema);
const RoleRateForm = ({
  employee,
  existSite,
  handleAddRateClose,
}) => {
  let theme = useTheme();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
  });
  const { AddMultiSelect,fetchSitesListForEmployee, editRoleRate } = useEmployees();
  const [multiSites, setMultiSites] = useState([]);
  const {
    state: { Names: sites },
    fetchSiteNames: fetchSites,
  } = useSite();
  useEffect(() => {
    fetchSites(employee?.company?.id);
  }, [fetchSites]);

  const getData = (name) => {
    switch (name) {
      case "sites":
        const filteredSites = sites?.filter(
          (s) => !employee?.sites?.flatMap((site) => site.siteId).includes(s.id)
        );

        return filteredSites
          ? [{ id: 0, name: "All Sites" }, ...filteredSites]
          : [];

      case "roles":
        return !existSite
          ? RolesEnum
          : RolesEnum?.filter(
            (s) =>
              !existSite?.roles
                .flatMap((role) => role.roleId)
                .includes(s.roleId)
          );
    }
  };
  useEffect(() => {
    if (existSite) {
      setValue("siteIds", [existSite?.siteId]);
    }
  }, [existSite, setValue]);

  const onSubmit = async (values) => {
    try {
      if (existSite) {
        await editRoleRate(employee?.id, { siteId: existSite?.id, roleId: values?.roleId, hourlyRate: values?.hourlyRate, zoneIds: [] })
      }
      else { await AddMultiSelect(employee?.id, values); }
      handleAddRateClose();
      fetchSitesListForEmployee(employee?.id,1,100);
    } catch (error) {
      console.log(error, "error");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography
        id="modal-modal-description"
        sx={{ textAlign: "center", fontWeight: 600, mb: 2 }}
      >
        {existSite?.siteId
          ? "Edit Role and Rate"
          : "Add New Role with Hourly Rate to specific Site"}
      </Typography>
      {existSite && existSite.siteId && (
        <Box sx={{ marginBottom: "10px" }}>
          <span style={{ fontSize: "12px" }}>Site Name</span>
          <Box
            sx={{
              borderBottom: "1px solid gray",
              width: "290px",
              borderRadius: "8px",
              textAlign: "center",
              marginRight: "20px",
              paddingTop: "25px",
            }}
          >
            {existSite?.siteName}
          </Box>
        </Box>
      )}
      {existSite?.siteId ? (
        <>
          {RoleRateFields.filter((f) => f.name === "roleId").map((f, index) => (
            <div key={index}>
              {f.select && (
                <FormControl
                  sx={{
                    width: theme.dimensions.width,
                    mt: theme.dimensions.mt,
                    mb: theme.dimensions.mb * 2,
                  }}
                  required
                  error={!!errors[f.name]}
                >
                  <InputLabel id={`select-label-${index}`}>
                    {f.label}
                  </InputLabel>
                  <Select
                    labelId={`select-label-${index}`}
                    id={`select-${index}`}
                    label={f.label}
                    size="small"
                    value={watch(f.name) ?? ""}
                    onChange={(event) => {
                      setValue(f.name, event.target.value);
                    }}
                  >
                    {getData(f.getData)?.map((s) => (
                      <MenuItem key={s.roleId} value={s.roleId}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {getData(f.getData)?.length === 0 && (
                    <p>no roles available</p>
                  )}
                </FormControl>
              )}
            </div>
          ))}
        </>
      ) : (
        <>
          {RoleRateFields.filter((f) => f.name === "siteIds").map(
            (f, index) => (
              <div key={index}>
                {f.select && (
                  <FormControl
                    sx={{
                      width: theme.dimensions.width,
                      mt: theme.dimensions.mt,
                      mb: theme.dimensions.mb * 2,
                    }}
                    required
                    error={!!errors[f.name]}
                  >
                    <InputLabel id={`sites-select-label-${index}`}>
                      {f.label}
                    </InputLabel>
                    <Select
                      labelId={`sites-select-label-${index}`}
                      id={`sites-select-${index}`}
                      label={f.label}
                      multiple
                      size="small"
                      value={multiSites ?? []}
                      onChange={(event) => {
                        const value = event.target.value;
                        setMultiSites(value);
                        setValue(f.name, value);
                      }}
                      renderValue={(selected) => {
                        const selectedArray = Array.isArray(selected)
                          ? selected
                          : [];
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "4px",
                            }}
                          >
                            {selectedArray.includes(0) ? (
                              <Chip label="All Sites" size="small" />
                            ) : (
                              selectedArray.map((value) => {
                                const site = sites?.find((s) => s.id === value);
                                return (
                                  <Chip
                                    key={value}
                                    label={site?.name || value}
                                    size="small"
                                  />
                                );
                              })
                            )}
                          </div>
                        );
                      }}
                    >
                      {getData(f.getData)?.map((s) => (
                        <MenuItem
                          key={s.id}
                          value={s.id}
                          disabled={
                            Array.isArray(multiSites) && multiSites.includes(0)
                          }
                        >
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {getData(f.getData)?.length === 0 && (
                      <p>no sites available</p>
                    )}
                  </FormControl>
                )}
              </div>
            )
          )}
          {RoleRateFields.filter((f) => f.name === "roleId").map((f, index) => (
            <div key={index}>
              {f.select && (
                <FormControl
                  sx={{
                    width: theme.dimensions.width,
                    mt: theme.dimensions.mt,
                    mb: theme.dimensions.mb * 2,
                  }}
                  required
                  error={!!errors[f.name]}
                >
                  <InputLabel id={`role-select-label-${index}`}>
                    {f.label}
                  </InputLabel>
                  <Select
                    labelId={`role-select-label-${index}`}
                    id={`role-select-${index}`}
                    label={f.label}
                    size="small"
                    value={watch(f.name) ?? ""}
                    onChange={(event) => {
                      setValue(f.name, event.target.value);
                    }}
                  >
                    {getData(f.getData)?.map((s) => (
                      <MenuItem key={s.roleId} value={s.roleId}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {getData(f.getData)?.length === 0 && (
                    <p>no roles available</p>
                  )}
                </FormControl>
              )}
            </div>
          ))}
        </>
      )}
      {RoleRateFields.filter((f) => f.number).map((f, index) => (
        <div key={index}>
          <TextField
            label={f.label}
            name={f.name}
            error={!!errors[f.name]}
            helperText={errors[f.name]?.message}
            required
            type="number"
            disabled={f.disabled}
            sx={{
              width: theme.dimensions.width,
              mt: theme.dimensions.mt,
              mb: theme.dimensions.mb * 6,
            }}
            inputProps={{
              ...register(f?.name),
              required: f.required,
            }}
            size="small"
          />
        </div>
      ))}

      <Button
        type="submit"
        sx={{
          background: theme.color.blackBackground,
          color: "white",
          width: "100%",
          mb: 1.3,
          "&:hover": { background: `${theme.color.black}` },
        }}
      >
        {existSite?.siteId ? "Update" : "Save"}
      </Button>
      <Button
        onClick={handleAddRateClose}
        sx={{
          background: theme.color.gray200,
          color: "black",
          width: "100%",
          "&:hover": { background: `${theme.color.gray300}` },
        }}
      >
        Cancel
      </Button>
    </form>
  );
};

export default RoleRateForm;
